import React from "react";
import { IconButton, NumericInput, NumericInputProps } from "..";
import { Icomoon } from "../../brand";

import "./NumberStepper.scss";

export interface NumberStepperProps extends NumericInputProps {
  title?: string;
}

/**
 * A numeric input component with buttons to increment and decrement the value. Built around `NumericInput`.
 * 
 * @status stable
 */
export const NumberStepper: React.FC<NumberStepperProps> = (props) => {
  const [spinnerValue, setSpinnerValue] = React.useState<number | undefined>(props.value);

  const onDecrementClick = React.useCallback(() => {
    const newVal = (spinnerValue ?? props.max ?? 0) - (props.step ?? 1);
    setSpinnerValue(newVal);
    props.onChange?.(newVal);
  }, [spinnerValue, props.onChange]);

  const onInputChange = React.useCallback((newVal: number | undefined) => {
    setSpinnerValue(newVal);
    props.onChange?.(newVal);
  }, [props.onChange]);

  const onIncrementClick = React.useCallback(() => {
    const newVal = (spinnerValue ?? props.min ?? 0) + (props.step ?? 1);
    setSpinnerValue(newVal);
    props.onChange?.(newVal);
  }, [spinnerValue, props.onChange]);

  return (
    <div className="number-stepper">
      <div className="number-stepper__inner">
        <IconButton
          className="number-stepper__minus"
          icomoon={Icomoon.minus2}
          onClick={onDecrementClick}
          disabled={props.disabled || (props.min != null && (spinnerValue ?? props.min ?? 0) <= props.min)}
          aria-hidden
        >
          <span className="is-sr-only">Decrease {props.title || ""} by {props.step || 1}</span>
        </IconButton>

        <NumericInput
          id={props.id}
          numberType={props.numberType ?? "integer"}
          value={spinnerValue}
          onChange={onInputChange}
          min={props.min}
          max={props.max}
          roundingExponent={props.roundingExponent}
        />

        <IconButton
          className="number-stepper__plus"
          icomoon={Icomoon.plus2}
          onClick={onIncrementClick}
          disabled={props.disabled || (props.max != null && (spinnerValue ?? props.min ?? 0) >= props.max)}
          aria-hidden
        >
          <span className="is-sr-only">Increase {props.title || ""} by {props.step || 1}</span>
        </IconButton>
      </div>
    </div>
  );
};

NumberStepper.defaultProps = {
  numberType: "integer",
  roundingExponent: -2
};
